/**
 * Automation Templates Registry
 *
 * Each template defines a pre-configured automation that users can
 * quickly launch by filling in a few fields. Templates produce a
 * complete taskConfig via buildTaskConfig(inputs).
 */

import { buildDefaultTaskConfig } from "../utils/configHelpers";

// ============================================================================
// GENERIC LIST SELECTORS
// ============================================================================
// Ordered set of XPath selectors for auto-detecting list containers on any page.
// Each uses (...)[1] to guarantee a single match (required by findView).
// Tried in order — first match wins.

export const GENERIC_LIST_SELECTORS = [
  // 1. ARIA feed — infinite scroll containers (social media, Google Maps, etc.)
  { type: "xpath", value: "(//*[@role='feed'])[1]" },

  // 2. ARIA list with listitem children
  { type: "xpath", value: "(//*[@role='list'][count(.//*[@role='listitem']) >= 3])[1]" },

  // 3. HTML lists scoped to <main> or role="main" (avoids nav menus)
  { type: "xpath", value: "(//main//ul[count(./li) >= 3] | //*[@role='main']//ul[count(./li) >= 3])[1]" },
  { type: "xpath", value: "(//main//ol[count(./li) >= 3] | //*[@role='main']//ol[count(./li) >= 3])[1]" },

  // 4. Data tables scoped to <main>
  { type: "xpath", value: "(//main//tbody[count(./tr) >= 3] | //*[@role='main']//tbody[count(./tr) >= 3])[1]" },

  // 5. Large HTML lists anywhere (high threshold to skip navigation)
  { type: "xpath", value: "(//ul[count(./li) >= 10])[1]" },
  { type: "xpath", value: "(//ol[count(./li) >= 5])[1]" },

  // 6. Large data tables anywhere
  { type: "xpath", value: "(//tbody[count(./tr) >= 5])[1]" },

  // 7. Deepest container with many repeated child elements
  //    The not(.//X[count(./Y)>=N]) pattern picks the most specific (innermost) match
  { type: "xpath", value: "(//*[count(./div) >= 10][not(.//*[count(./div) >= 10])])[1]" },
  { type: "xpath", value: "(//*[count(./article) >= 3][not(.//*[count(./article) >= 3])])[1]" },
  { type: "xpath", value: "(//*[count(./a) >= 10][not(.//*[count(./a) >= 10])])[1]" },
];

// ============================================================================
// TEMPLATE DEFINITIONS
// ============================================================================

export const TEMPLATES = [
  {
    id: "list-scraper",
    name: "Universal List Scraper",
    description: "Auto-detect and extract list data from any website URL",
    category: "General",
    icon: "list",
    fields: [
      {
        id: "url",
        label: "Page URL",
        placeholder: "e.g. https://example.com/products",
        required: true,
        type: "url",
      },
      {
        id: "name",
        label: "Automation Name",
        placeholder: "e.g. Product listings",
        required: true,
        type: "text",
      },
    ],
    buildTaskConfig(inputs) {
      const { url, name } = inputs;

      let favicon = "";
      try {
        const origin = new URL(url).origin;
        favicon = `${origin}/favicon.ico`;
      } catch { /* invalid URL, skip */ }

      const config = buildDefaultTaskConfig("listExtractor");
      config.metadata = {
        title: name,
        websiteUrl: url,
        websiteFavicon: favicon,
      };
      config.general = {
        ...config.general,
        name,
        websiteUrl: url,
      };
      config.automation = {
        url,
        listSelectors: GENERIC_LIST_SELECTORS,
        paginationMethod: "scroll",
        paginationSelectors: [],
        automationOptions: {
          maxContentNoChange: 3,
          maxPagesToProcess: -1,
          maxWaitForContentChange: 5000,
        },
      };
      return config;
    },
  },
  {
    id: "google-maps-scraper",
    name: "Google Maps Scraper",
    description: "Scrape business listings from Google Maps for any location and keyword",
    category: "Search Engines",
    icon: "maps", // rendered by the UI as an icon key
    fields: [
      {
        id: "keyword",
        label: "Search Keyword",
        placeholder: "e.g. restaurants, dentists, hotels",
        required: true,
        type: "text",
      },
      {
        id: "city",
        label: "City / Location",
        placeholder: "e.g. San Francisco, London, Tokyo",
        required: true,
        type: "text",
      },
    ],
    buildTaskConfig(inputs) {
      const { keyword, city } = inputs;
      const encodedQuery = encodeURIComponent(`${keyword} in ${city}`);
      const url = `https://www.google.com/maps/search/${encodedQuery}`;

      const config = buildDefaultTaskConfig("listExtractor");
      config.metadata = {
        title: `Google Maps - ${keyword} in ${city}`,
        websiteUrl: url,
        websiteFavicon: "https://www.google.com/favicon.ico",
      };
      config.general = {
        ...config.general,
        name: `Google Maps - ${keyword} in ${city}`,
        websiteUrl: url,
      };
      config.automation = {
        url,
        listSelectors: [{ type: "css", value: "div[role='feed']" }],
        paginationMethod: "scroll",
        paginationSelectors: [],
        automationOptions: {
          maxContentNoChange: 3,
          maxPagesToProcess: -1,
          maxWaitForContentChange: 5000,
        },
      };
      return config;
    },
  },
  {
    id: "chrome-store-scraper",
    name: "Chrome Web Store Scraper",
    description: "Scrape extension listings from the Chrome Web Store",
    category: "App Stores",
    icon: "chrome", // rendered by the UI as an icon key
    fields: [
      {
        id: "keyword",
        label: "Search Keyword",
        placeholder: "e.g. vpn, ad blocker, productivity",
        required: true,
        type: "text",
      },
      {
        id: "language",
        label: "Language (optional)",
        placeholder: "e.g. en, fr, de, es",
        required: false,
        type: "text",
      },
    ],
    buildTaskConfig(inputs) {
      const { keyword, language } = inputs;
      const encodedKeyword = encodeURIComponent(keyword);
      let url = `https://chromewebstore.google.com/search/${encodedKeyword}`;
      if (language?.trim()) {
        url += `?hl=${encodeURIComponent(language.trim())}`;
      }

      const config = buildDefaultTaskConfig("listExtractor");
      config.metadata = {
        title: `Chrome Store - ${keyword}`,
        websiteUrl: url,
        websiteFavicon: "https://chromewebstore.google.com/favicon.ico",
      };
      config.general = {
        ...config.general,
        name: `Chrome Store - ${keyword}`,
        websiteUrl: url,
      };
      config.automation = {
        url,
        listSelectors: [{ type: "css", value: "div.Cb7Kte" }],
        paginationMethod: "scroll",
        paginationSelectors: [],
        automationOptions: {
          maxContentNoChange: 3,
          maxPagesToProcess: -1,
          maxWaitForContentChange: 5000,
        },
      };
      return config;
    },
  },
];

/**
 * Get a template by ID
 */
export const getTemplateById = (id) => TEMPLATES.find((t) => t.id === id);
